import type { MusicalToken, TimeSignature } from '../../types/music';
import { buildBarBeatStructure } from '../rhythmicCellEngine';
import type { PhraseScoreBar } from './VexPhraseScore';
import type { VexCellRange } from './vexBeamBuilder';
import {
  mapEventsToVexSpecs,
  mapRhythmicCellToVexSpecs,
  truncateSpecsToBarCapacity,
  type VexNoteSpec,
} from './vexFlowMapper';

function beatsFromTimeSignature(ts: TimeSignature): number {
  const [n, d] = ts.split('/').map(Number);
  return (n || 4) * (4 / (d || 4));
}

export function cellRangesFromBeatCells(
  beatCells: Array<{ tokens: MusicalToken[]; modifiers?: { isSpanContinuation?: boolean; isPadding?: boolean } }>,
  specCount: number,
): VexCellRange[] {
  const ranges: VexCellRange[] = [];
  let cursor = 0;

  for (let beatIdx = 0; beatIdx < beatCells.length; beatIdx += 1) {
    const cell = beatCells[beatIdx]!;
    if (cell.modifiers?.isSpanContinuation || cell.modifiers?.isPadding) continue;

    const start = cursor;
    cursor += Math.max(1, cell.tokens.length);
    if (start >= specCount) break;
    ranges.push({ start, end: Math.min(cursor, specCount), beatIndex: beatIdx });
  }

  return ranges;
}

/** Specs + rangos de célula para un compás de frase (beaming determinista + pitch fiel). */
export function buildLayoutForPhraseBar(
  bar: PhraseScoreBar,
  timeSignature: TimeSignature,
): { specs: VexNoteSpec[]; cellRanges: VexCellRange[] } {
  const maxBeats = beatsFromTimeSignature(timeSignature);

  if (bar.scoreEvents?.length) {
    const mapped = mapEventsToVexSpecs(bar.scoreEvents);
    const specs = truncateSpecsToBarCapacity(mapped.specs, maxBeats);
    const tokens = bar.scoreEvents.map((e) => e.token);
    const { beatCells } = buildBarBeatStructure(tokens, timeSignature, {
      dottedIndices: bar.dottedIndices,
      tiePairs: bar.intraBarTies,
    });
    const cellRanges = cellRangesFromBeatCells(beatCells, specs.length);
    return { specs, cellRanges };
  }

  const tokens = bar.patternTokens ?? [];
  if (!tokens.length) return { specs: [], cellRanges: [] };

  const { beatCells } = buildBarBeatStructure(tokens, timeSignature, {
    dottedIndices: bar.dottedIndices,
    tiePairs: bar.intraBarTies,
  });

  const specs: VexNoteSpec[] = [];
  const cellRanges: VexCellRange[] = [];

  for (let beatIdx = 0; beatIdx < beatCells.length; beatIdx += 1) {
    const cell = beatCells[beatIdx]!;
    if (cell.modifiers?.isSpanContinuation || cell.modifiers?.isPadding) continue;

    const start = specs.length;
    specs.push(...mapRhythmicCellToVexSpecs(cell, cell.modifiers?.hasDot === true));
    cellRanges.push({ start, end: specs.length, beatIndex: beatIdx });
  }

  const capped = truncateSpecsToBarCapacity(specs, maxBeats);
  const trimmedRanges = cellRanges
    .map((range) => ({ ...range, end: Math.min(range.end, capped.length) }))
    .filter((range) => range.start < range.end && range.start < capped.length);

  return { specs: capped, cellRanges: trimmedRanges };
}

export function flattenPhraseBarLayouts(
  layouts: Array<{ specs: VexNoteSpec[]; cellRanges: VexCellRange[] }>,
): { specs: VexNoteSpec[]; cellRanges: VexCellRange[] } {
  const specs: VexNoteSpec[] = [];
  const cellRanges: VexCellRange[] = [];
  let offset = 0;

  for (const layout of layouts) {
    specs.push(...layout.specs);
    for (const range of layout.cellRanges) {
      cellRanges.push({
        start: offset + range.start,
        end: offset + range.end,
        beatIndex: range.beatIndex,
      });
    }
    offset += layout.specs.length;
  }

  return { specs, cellRanges };
}
